import { useEffect, useState } from 'react'
import {
  I18nContext,
  type I18nContextValue,
  type Translate,
  type TranslateVars,
} from './context'
import { catalogs } from './messages'
import {
  DEFAULT_LANGUAGE,
  LOCALE_STORAGE_KEY,
  getInitialLocale,
  type LanguageCode,
} from './languages'

/** Replace `{name}` tokens in a message with the provided values. */
function interpolate(message: string, vars: TranslateVars): string {
  return message.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  )
}

/**
 * App-wide i18n provider. Holds the active locale, persists it to localStorage,
 * and keeps `<html lang>` in sync for accessibility and correct hyphenation.
 *
 * Wrap the app once near the root; components read translations with `useI18n()`.
 */
export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<LanguageCode>(getInitialLocale)

  useEffect(() => {
    document.documentElement.lang = locale
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
      // Persisting is best-effort; ignore storage failures (private mode etc.).
    }
  }, [locale])

  const t: Translate = (key, vars) => {
    const message = catalogs[locale][key] ?? catalogs[DEFAULT_LANGUAGE][key] ?? key
    return vars ? interpolate(message, vars) : message
  }

  const value: I18nContextValue = { locale, setLocale, t }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
